/**
 * The color NAMES. Nothing else.
 *
 * A name becomes a color in CSS: put `data-color={color}` on the element and
 * use the static utilities `text-c`, `bg-c`, `bg-c-soft`, `text-c-foreground`,
 * `fill-c`, `stroke-c`. The `[data-color="…"]` rules in `tokens.css` bind the
 * name to its tokens, in both modes, live.
 *
 * Keep it that way: Tailwind's scanner only emits classes written out verbatim,
 * so a per-name class map cannot be generated from this list, and a JS resolver
 * cannot beat a var() the browser already re-resolves on its own.
 */

export const COLORS = [
  // Semantic
  "primary",
  "secondary",
  "destructive",
  "muted",
  "accent",
  "success",
  "warning",
  "info",
  // Palette — entities persist these names
  "dark-grey",
  "grey",
  "light-grey",
  "red",
  "orange",
  "yellow",
  "light-green",
  "green",
  "teal",
  "cyan",
  "light-blue",
  "blue",
  "purple",
  "light-purple",
  "violet",
  "magenta",
  "pink",
] as const;

export type Color = (typeof COLORS)[number];

const COLOR_SET: ReadonlySet<string> = new Set(COLORS);

/**
 * Whether an arbitrary string names a color on this scale.
 *
 * Persisted entity colors arrive as free-form strings because the column is a
 * string. This guard and {@link toColor} are the ONLY sanctioned way to turn
 * one into a {@link Color} — never `as Color`. Past that gate every prop,
 * field and variable that carries a color is typed `Color`, so a value the
 * scale does not know cannot reach a component that assumes it does.
 */
export function isColor(value: string | null | undefined): value is Color {
  return value != null && COLOR_SET.has(value);
}

/**
 * {@link isColor} in the shape a boundary wants: narrow a stored string to a
 * name on the scale, or `undefined` when it is not one.
 */
export function toColor(value: string | null | undefined): Color | undefined {
  return isColor(value) ? value : undefined;
}

/** Count of leading semantic entries in {@link COLORS}. */
const SEMANTIC_COUNT = 8;

/**
 * The palette subset — exactly the names entities persist. Derived from
 * {@link COLORS} so the two can never disagree.
 */
export const PALETTE_COLORS: readonly Color[] = COLORS.slice(SEMANTIC_COUNT);

/**
 * A `var()` reference to the token behind `color`, for the few places that take
 * a color as a STRING: a chart series accessor, an SVG `fill`/`stroke`/
 * `stop-color` prop on a component that forwards it as an attribute.
 *
 * `fill`, `stroke` and `stop-color` are CSS properties. They resolve `var()`
 * and `color-mix()` and they re-resolve on a theme flip, so a string built here
 * stays live — there is nothing to recompute in JS.
 *
 * The rules mirror `[data-color]` in `tokens.css`: `secondary`, `muted` and
 * `accent` are surface tokens, so as a mark color they hand back their foreground pair.
 */
export function colorVar(color: Color): string {
  if (color === "secondary") return "var(--secondary-foreground)";
  if (color === "muted") return "var(--muted-foreground)";
  if (color === "accent") return "var(--accent-foreground)";
  return `var(--${color})`;
}

/** {@link colorVar} at `alpha` opacity. */
export function colorVarAlpha(color: Color, alpha: number): string {
  return `color-mix(in oklab, ${colorVar(color)} ${alpha * 100}%, transparent)`;
}
